import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import Link from 'next/link'
import { useRouter } from 'next/router'

import { cx } from '@linaria/core'
import { addMonths } from 'date-fns'
import ja from 'date-fns/locale/ja'
import ReactDatePicker, { registerLocale } from 'react-datepicker'
import { useErrorBoundary } from 'react-error-boundary'
import { z } from 'zod'

import { useFoodMutationAsync } from '~/api-client/food_api'
import { dinnerNewDinner } from '~/api-client/food_api/dinner'
import { useSearchRestaurants } from '~/api-client/food_api/restaurant'
import {
  Dinner,
  DinnerMemo,
  Image,
  Restaurant,
} from '~/api-definition/definition'
import { DinnerOptionListResponse } from '~/api-definition/response'
import { Button } from '~/components/action/Button'
import { Modal } from '~/components/action/Modal'
import { AppImage } from '~/components/data-display/AppImage'
import { Heading } from '~/components/data-display/Heading'
import { Loading } from '~/components/data-display/Loading'
import { Checkbox } from '~/components/data-input/Checkbox'
import { Label } from '~/components/data-input/Label'
import { SelectV2 } from '~/components/data-input/SelectV2'
import { TextArea, TextField } from '~/components/data-input/TextField'
import {
  ArrowLeftSmallIcon,
  ArrowRightSmallIcon,
  CalendarIcon,
  ChevronRightIcon,
  CloseIcon,
  TipsIcon,
} from '~/components/icons'
import { Container } from '~/components/layout/Container'
import { headerHeight } from '~/components/layout/Header'
import { config } from '~/config'
import { formatDate } from '~/libs/date'
import {
  MinimumDinnerMember,
  dinnerMemberTypeLabels,
  dinnerThumbnailImageOptToSrc,
  dinnerThumbnailImageToSrc,
} from '~/libs/dinner'
import { getFormValueString } from '~/libs/form'
import { preprocessImages } from '~/libs/image'
import { anyNgWordIncluded } from '~/libs/ng_words'
import { restaurantThumbnailImages } from '~/libs/restaurant'
import { extractUrlOpt } from '~/libs/url'
import { zPreprocessIntString } from '~/libs/zod'

import { ShowOnly } from '../ShowOnly'
import { AddImage } from '../image/AddImage'
import { MemberSelectModal } from '../member/MemberSelectModal'
import { DictionaryDialog } from './dictionary/DictionaryDialog'

registerLocale('ja', ja)

const getJSTDate = () => {
  const now = new Date()
  const utc = now.getTime() + now.getTimezoneOffset() * 60000 // convert to UTC
  return new Date(utc + 3600000 * 9) // convert UTC to JST (9 hours ahead)
}

const convertDateYYYYMMDD = (date: Date) => {
  const yyyy = date.getFullYear().toString()
  const mm = (date.getMonth() + 1).toString()
  const dd = date.getDate().toString()

  const mmChars = mm.split('')
  const ddChars = dd.split('')

  return (
    yyyy +
    '-' +
    (mmChars[1] ? mm : '0' + mmChars[0]) +
    '-' +
    (ddChars[1] ? dd : '0' + ddChars[0])
  )
}

const createJstDateStr = (date: Date, h: number, m: number) => {
  const zerofilledHours = ('00' + h).slice(-2)
  const zerofilledMinutes = ('00' + m).slice(-2)
  const jstDateStr =
    convertDateYYYYMMDD(date) +
    'T' +
    zerofilledHours +
    ':' +
    zerofilledMinutes +
    ':00+09:00'

  return jstDateStr
}
const DatePicker: React.FC<{
  startDate?: Date | null
  setStartDate: (date: Date | null) => void
  setIsDatepickerOpen: (boolean: boolean) => void
}> = (props) => {
  return (
    <div>
      <ReactDatePicker
        inline
        locale={ja}
        dateFormat="YYYY-MM-DD"
        selected={props.startDate}
        minDate={getJSTDate()}
        onChange={(date) => {
          props.setStartDate(date)
          props.setIsDatepickerOpen(false)
        }}
        renderCustomHeader={({ date, decreaseMonth, increaseMonth }) => {
          const prevMonth = addMonths(date, -1)
          const nextMonth = addMonths(date, 1)
          return (
            <div className="flex items-center justify-between my-4 px-14 text-base">
              <button
                type="button"
                className="flex items-center gap-1 border border-black rounded-sm px-2 py-1 font-bold text-xs"
                onClick={decreaseMonth}
              >
                <ArrowLeftSmallIcon className="fill-black w-4 h-4" />
                {prevMonth.getMonth() + 1}月
              </button>
              <div className="flex justify-center font-bold">
                <div>{date.getFullYear()}年</div>
                <div>{date.getMonth() + 1}月</div>
              </div>
              <button
                type="button"
                className="flex items-center gap-1 border border-black rounded-sm px-2 py-1 font-bold text-xs"
                onClick={increaseMonth}
              >
                {nextMonth.getMonth() + 1}月{' '}
                <ArrowRightSmallIcon className="fill-black w-4 h-4" />
              </button>
            </div>
          )
        }}
      />
    </div>
  )
}

const FormDataSchema = z.object({
  dinner_name: z.string().refine(
    (value) => {
      return !anyNgWordIncluded(value)
    },
    {
      message: 'NGワードが含まれています',
    }
  ),
  scheduled_at_hours: z.preprocess(zPreprocessIntString, z.number()),
  scheduled_at_minutes: z.preprocess(zPreprocessIntString, z.number()),
  approvement_scheduled_at_hours: z.preprocess(
    zPreprocessIntString,
    z.number()
  ),
  approvement_scheduled_at_minutes: z.preprocess(
    zPreprocessIntString,
    z.number()
  ),
  reservation_number: z.preprocess(zPreprocessIntString, z.number()),
  recruitment_number: z.preprocess(zPreprocessIntString, z.number()),
  privacy_type: z.string(),
  fixed_budget_amount: z.preprocess(
    zPreprocessIntString,
    z
      .number({ invalid_type_error: '金額は数字で入力してください' })
      .nullable()
      .optional()
      .transform((v) => (v === null ? undefined : v))
  ),
  min_budget_amount: z.preprocess(
    zPreprocessIntString,
    z
      .number()
      .nullable()
      .optional()
      .transform((v) => (v === null ? undefined : v))
  ),
  max_budget_amount: z.preprocess(
    zPreprocessIntString,
    z
      .number()
      .nullable()
      .optional()
      .transform((v) => (v === null ? undefined : v))
  ),
  thumbnail_image_ids: z
    .array(z.string())
    .nullable()
    .optional()
    .transform((v) => (v === null ? undefined : v)),
  restaurant_name: z
    .string()
    .min(1, '店名を入力してください')
    .refine(
      (value) => {
        return !anyNgWordIncluded(value)
      },
      {
        message: 'NGワードが含まれています',
      }
    ),
  restaurant_address: z
    .string()
    .min(1, '住所を入力してください')
    .refine(
      (value) => {
        return !anyNgWordIncluded(value)
      },
      {
        message: 'NGワードが含まれています',
      }
    ),
  restaurant_url: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === null ? undefined : v)),
  note: z
    .string()
    .refine(
      (value) => {
        return !anyNgWordIncluded(value)
      },
      {
        message: 'NGワードが含まれています',
      }
    )
    .nullable()
    .optional()
    .transform((v) => (v === null ? undefined : v)),
  memo_text: z
    .string()
    .nullable()
    .optional()
    .transform((v) => (v === null ? undefined : v)),
  min_age: z.preprocess(
    zPreprocessIntString,
    z
      .number()
      .nullable()
      .optional()
      .transform((v) => (v === null ? undefined : v))
  ),
  max_age: z.preprocess(
    zPreprocessIntString,
    z
      .number()
      .nullable()
      .optional()
      .transform((v) => (v === null ? undefined : v))
  ),
  favorite_alcohol_type: z.string(),
  alcohol_amount_type: z.string(),
  food_amount_type: z.string(),
  sex_type: z.string(),
  payment_type: z.string(),
})

const FormDataWithRestaurant = FormDataSchema.omit({
  restaurant_name: true,
  restaurant_address: true,
}).extend({
  restaurant_id: z.string(),
  restaurant_address: z
    .string()
    .optional()
    .nullable()
    .transform((v) => (v === null ? undefined : v)),
})

const FormSchema = FormDataSchema.or(FormDataWithRestaurant).refine(
  ({ reservation_number, recruitment_number }) =>
    reservation_number >= recruitment_number,
  {
    path: ['reservation_number'],
    message: '予約人数を正しく入力してください',
  }
)

const amountMaxLimit1 = 100000
const amountUnit1 = 1000

const amountMaxLimit2 = 300000
const amountUnit2 = 10000

const budgetOptions = Array.from(
  {
    length: Math.floor(amountMaxLimit1 / amountUnit1),
  },
  (_, i) => {
    const amount = (i + 1) * amountUnit1
    return { value: String(amount), label: amount.toLocaleString() }
  }
).concat(
  Array.from(
    {
      length: Math.floor((amountMaxLimit2 - amountMaxLimit1) / amountUnit2),
    },
    (_, i) => {
      const amount = amountMaxLimit1 + (i + 1) * amountUnit2
      return { value: String(amount), label: amount.toLocaleString() }
    }
  )
)

const DinnerForm: React.FC<{
  dinner?: Dinner
  dinnerMemo?: DinnerMemo
  staticDinnerOptionListResponse: DinnerOptionListResponse
  onSubmit: (body: Parameters<typeof dinnerNewDinner>[0]['body']) => void
}> = (props) => {
  const { showBoundary } = useErrorBoundary()
  const newDinnerThubnailImage = useFoodMutationAsync(
    '/dinner/new_dinner_thumbnail_image'
  )

  const now = useMemo(() => getJSTDate(), [])
  const scheduledAtDefaultValue = useMemo(() => {
    if (now.getHours() >= 23) {
      const d = new Date(now.getTime() + 24 * 60 * 60 * 1000)
      d.setHours(0)
      d.setMinutes(0)
      return d
    } else if (now.getHours() >= 18) {
      const d = new Date(now.getTime())
      d.setHours(now.getHours() + 1)
      d.setMinutes(0)
      return d
    } else {
      const d = new Date(now.getTime())
      d.setHours(18)
      d.setMinutes(0)
      return d
    }
  }, [now])

  const router = useRouter()

  const onOpenScheduledAtCalendarAnchor = useRef<HTMLDivElement>(null)
  const onOpenApprovementScheduledAtCalendarAnchor =
    useRef<HTMLDivElement>(null)

  const formRef = useRef<HTMLFormElement>(null)
  const noteInputRef = useRef<HTMLTextAreaElement>(null)

  const [openScheduledAtControlledSelect, setOpenScheduledAtControlledSelect] =
    useState<'hours' | 'minutes'>()
  const [
    openApprovementScheduledAtControlledSelect,
    setOpenApprovementScheduledAtControlledSelect,
  ] = useState<'hours' | 'minutes'>()

  const [formError, setFormError] =
    useState<z.ZodError<z.infer<typeof FormSchema>>>()
  const flattenErrors = formError?.flatten().fieldErrors
  const hasError = (formError?.errors.length ?? 0) > 0

  // 支払い方法の制限部分は、1つしか選べない一方チェックを外すこともできるという仕様のため
  // radio も checkbox もそのまま使うのに適さない。よって自前で state を管理する
  const [paymentOption, setPaymentOption] = useState<string>(
    props.dinner?.paymentOption.key ?? 'unselected'
  )

  const [minBudgetAmount, setMinBudgetAmount] = useState(
    props.dinner
      ? props.dinner.minBudgetAmount
        ? String(props.dinner.minBudgetAmount)
        : ''
      : '20000'
  )
  const [maxBudgetAmount, setMaxBudgetAmount] = useState(
    props.dinner
      ? props.dinner.maxBudgetAmount
        ? String(props.dinner.maxBudgetAmount)
        : ''
      : '25000'
  )

  const minBudgetAmountNumber = parseInt(minBudgetAmount)
  const maxBudgetAmountNumber = parseInt(maxBudgetAmount)

  // モーダルが消えるアニメーションの最中にモーダル内のコンテンツだけが消えるのを回避するために
  // isOpen フラグを利用する。
  const [modal, setModal] = useState<{
    isOpen: boolean
    title: string
    body:
      | string
      | { type: 'sections'; data: { header?: string; body: string }[] }
  }>()

  const [isUploadingImages, setIsUploadingImages] = useState(false)
  const [uploadingImagesError, setUploadingImagesError] = useState<string>()
  const [isInvitationModalOpen, setIsInvitationModalOpen] = useState(false)
  const [isScheduledAtDatepickerOpen, setIsScheduledAtDatepickerOpen] =
    useState(false)
  const [
    isApprovementScheduledAtDatepickerOpen,
    setIsApprovementScheduledAtDatepickerOpen,
  ] = useState(false)
  const [isFixedBudgetAmount, setIsFixedBudgetAmount] = useState(
    props.dinner?.fixedBudgetAmount != null
  )

  const [restaurant, setRestaurant] = useState<Restaurant | undefined>(
    props.dinner?.restaurant
  )

  const [dinnerThumbnailImages, setDinnerThumbnailImages] = useState<Image[]>(
    props.dinner?.thumbnailImages ?? []
  )
  const [preferDinnerThumbnailImages, setPreferDinnerThumbnailImages] =
    useState(dinnerThumbnailImages.length > 0)

  const [searchRestaurantsQuery, setSearchRestaurantsQuery] = useState('')
  const [triedSubmit, setTriedSubmit] = useState(false)

  const [selectedMembers, setSelectedMembers] = useState<MinimumDinnerMember[]>(
    props.dinner?.members ?? []
  )
  const [oldSelectedMembers, setOldSelectedMembers] =
    useState<MinimumDinnerMember[]>(selectedMembers)
  const searchRestaurants = useSearchRestaurants({
    searchParams: {
      key: searchRestaurantsQuery,
      count: config.pageSize.restaurantSearchList,
    },
  })

  const linkedRestaurantThumbnailImages = useMemo(
    () =>
      restaurant == null ? undefined : restaurantThumbnailImages(restaurant, 3),
    [restaurant]
  )
  const usingDinnerThumbnailImages = useMemo(
    () =>
      preferDinnerThumbnailImages ||
      (props.dinner == null &&
        (restaurant == null || restaurant.thumbnailImages.length === 0)),
    [preferDinnerThumbnailImages, props.dinner, restaurant]
  )

  const scheduledAt = useMemo(() => {
    if (props.dinner?.scheduledAt) {
      const dateUTC = new Date(props.dinner.scheduledAt * 1000) // Create date in LocalTime
      const offset = dateUTC.getTimezoneOffset() * 60000 // Get the timezone offset in milliseconds
      const jstTime = new Date(dateUTC.getTime() + offset + 3600000 * 9) // Convert to JST (9 hours ahead of UTC)
      return jstTime
    }
    return null
  }, [props.dinner])

  const [scheduledDate, setScheduledDate] = useState<Date | null>(
    scheduledAt ?? scheduledAtDefaultValue
  )

  const [scheduledAtHours, scheduledAtMinutes] = useMemo(() => {
    if (scheduledAt) {
      return [scheduledAt.getHours(), scheduledAt.getMinutes()]
    } else {
      return [
        scheduledAtDefaultValue.getHours(),
        scheduledAtDefaultValue.getMinutes(),
      ]
    }
  }, [scheduledAt, scheduledAtDefaultValue])

  const approvementScheduledAt = useMemo(() => {
    if (props.dinner?.approvementScheduledAt) {
      const dateUTC = new Date(props.dinner.approvementScheduledAt * 1000) // Create date in LocalTime
      const offset = dateUTC.getTimezoneOffset() * 60000 // Get the timezone offset in milliseconds
      const jstTime = new Date(dateUTC.getTime() + offset + 3600000 * 9) // Convert to JST (9 hours ahead of UTC)
      return jstTime
    }
    return null
  }, [props.dinner])

  const [approvementScheduledDate, setApprovementScheduledDate] =
    useState<Date | null>(approvementScheduledAt)

  const [approvementScheduledAtHours, approvementScheduledAtMinutes] =
    useMemo(() => {
      if (approvementScheduledDate) {
        return [
          approvementScheduledDate.getHours(),
          approvementScheduledDate.getMinutes(),
        ]
      } else {
        return [0, 0]
      }
    }, [approvementScheduledDate])

  const onInputThumnailImages = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUploadingImagesError(undefined)

    if (e.currentTarget.files == null) {
      return
    }

    const files = Array.from(e.currentTarget.files)

    // NOTE: 次に同じファイルを選択し直した時に onInput が発火しなくなるのでクリアする
    e.currentTarget.value = ''

    if (files.length === 0) {
      return
    }

    const uploadableCount = 3 - (dinnerThumbnailImages?.length ?? 0)
    if (files.length > uploadableCount) {
      setUploadingImagesError('設定できる写真は3枚までです')
      return
    }

    setIsUploadingImages(true)
    try {
      const pImages = await preprocessImages(files, 'dinner_thumbnail')
      if (pImages == null) return
      const responses = await Promise.all(
        pImages.map(async (file) => {
          return newDinnerThubnailImage({
            imageFile: file,
          })
        })
      )
      const images = responses.map((r) => r.image)
      console.log(images)
      setDinnerThumbnailImages((prev) => (prev ? prev.concat(images) : images))
    } catch (e) {
      showBoundary(e)
    } finally {
      setIsUploadingImages(false)
    }
  }

  const parseFormData = useCallback(
    (formData: FormData) => {
      const parsed = FormSchema.refine(
        ({ scheduled_at_hours, scheduled_at_minutes }) => {
          const scheduledAt = new Date(scheduledDate ?? new Date())
          scheduledAt.setHours(scheduled_at_hours)
          scheduledAt.setMinutes(scheduled_at_minutes)
          return scheduledAt.getTime() > Date.now()
        },
        {
          path: ['scheduled_at_hours'],
          message: '開始日時に過去の日時は指定できません',
        }
      )
        .refine(
          ({
            approvement_scheduled_at_hours,
            approvement_scheduled_at_minutes,
          }) => {
            if (approvementScheduledDate == null) return true
            const approvementScheduledAt = new Date(
              approvementScheduledDate ?? new Date()
            )
            approvementScheduledAt.setHours(approvement_scheduled_at_hours ?? 0)
            approvementScheduledAt.setMinutes(
              approvement_scheduled_at_minutes ?? 0
            )
            return approvementScheduledAt.getTime() > Date.now()
          },
          {
            path: ['approvement_scheduled_at_hours'],
            message: '参加者決定の予定期日に過去の日時は指定できません',
          }
        )
        .refine(
          ({
            scheduled_at_hours,
            scheduled_at_minutes,
            approvement_scheduled_at_hours,
            approvement_scheduled_at_minutes,
          }) => {
            if (approvementScheduledDate == null) return true
            const approvementScheduledAt = new Date(
              approvementScheduledDate ?? new Date()
            )
            approvementScheduledAt.setHours(approvement_scheduled_at_hours ?? 0)
            approvementScheduledAt.setMinutes(
              approvement_scheduled_at_minutes ?? 0
            )
            const scheduledAt = new Date(scheduledDate ?? new Date())
            scheduledAt.setHours(scheduled_at_hours)
            scheduledAt.setMinutes(scheduled_at_minutes)
            return scheduledAt.getTime() > approvementScheduledAt.getTime()
          },
          {
            path: ['approvement_scheduled_at_hours'],
            message:
              '参加者決定の予定日時は、食事会の予定より以前の日時を指定してください。',
          }
        )
        //
        .safeParse({
          dinner_name: formData.get('dinner_name'),
          scheduled_at_hours: formData.get('scheduled_at_hours'),
          scheduled_at_minutes: formData.get('scheduled_at_minutes'),
          approvement_scheduled_at_hours: formData.get(
            'approvement_scheduled_at_hours'
          ),
          approvement_scheduled_at_minutes: formData.get(
            'approvement_scheduled_at_minutes'
          ),
          reservation_number: formData.get('reservation_number'),
          recruitment_number: formData.get('recruitment_number'),
          privacy_type: formData.get('privacy_type'),
          min_budget_amount: isFixedBudgetAmount
            ? undefined
            : formData.get('min_budget_amount'),
          max_budget_amount: isFixedBudgetAmount
            ? undefined
            : formData.get('max_budget_amount'),
          fixed_budget_amount: isFixedBudgetAmount
            ? formData.get('fixed_budget_amount')
            : undefined,
          thumbnail_image_ids: dinnerThumbnailImages
            ? dinnerThumbnailImages.map((i) => i.imageId)
            : undefined,
          restaurant_id: restaurant?.restaurantId,
          restaurant_name:
            restaurant == null ? formData.get('restaurant_name') : undefined,
          // 既に dinner.restaurantAddress が設定されている、もしくはrestaurant.address と formData.get('restaurant_address') が一致している
          // 場合は undefined を送る。
          restaurant_address: (() => {
            if (restaurant == null) return formData.get('restaurant_address')

            if (props.dinner == null) {
              if (restaurant.address === formData.get('restaurant_address')) {
                // use restaurant address
                return undefined
              } else {
                return formData.get('restaurant_address')
              }
            } else {
              if (
                props.dinner.restaurantAddress ===
                formData.get('restaurant_address')
              ) {
                // no changes
                return undefined
              } else {
                return formData.get('restaurant_address')
              }
            }
          })(),
          restaurant_url: (() => {
            const restaurantFormValue = extractUrlOpt(
              getFormValueString(formData, 'restaurant_url')
            )

            if (restaurant == null) return restaurantFormValue

            if (props.dinner == null) {
              if (restaurant.tabelogRestaurantUrl === restaurantFormValue) {
                // use restaurant address
                return undefined
              } else {
                return restaurantFormValue
              }
            } else {
              if (props.dinner.restaurantUrl === restaurantFormValue) {
                // no changes
                return undefined
              } else {
                return restaurantFormValue
              }
            }
          })(),
          favorite_alcohol_type: formData.get('favorite_alcohol_type'),
          alcohol_amount_type: formData.get('alcohol_amount_type'),
          food_amount_type: formData.get('food_amount_type'),
          sex_type: formData.get('sex_type'),
          payment_type: paymentOption,
          min_age: formData.get('min_age'),
          max_age: formData.get('max_age'),
          note: formData.get('note'),
          memo_text: formData.get('memo_text'),
        })
      if (!parsed.success) {
        console.log(parsed.error)
      }
      return parsed
    },
    [
      isFixedBudgetAmount,
      dinnerThumbnailImages,
      restaurant,
      paymentOption,
      scheduledDate,
      approvementScheduledDate,
      props.dinner,
    ]
  )

  const clearForm = () => {
    formRef.current?.reset()
    setTriedSubmit(false)
    setPaymentOption(props.dinner?.paymentOption.key ?? 'unselected')
    setIsInvitationModalOpen(false)
    setIsScheduledAtDatepickerOpen(false)
    setIsFixedBudgetAmount(props.dinner?.fixedBudgetAmount != null)
    setRestaurant(undefined)
    setSearchRestaurantsQuery('')
    setScheduledDate(scheduledAt ?? getJSTDate())
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    if (formRef.current) {
      const data = parseFormData(new FormData(formRef.current))
      if (data.success) {
        setFormError(undefined)
      } else {
        setFormError(data.error)
      }
    }
  }, [parseFormData])

  return (
    <div className="h-full relative">
      {<Loading loading={isUploadingImages} loadingText={'Uploading'} />}
      <Modal
        isOpen={modal != null && modal.isOpen}
        title={modal?.title}
        body={
          modal?.body == null ? undefined : typeof modal.body === 'string' ? (
            modal.body
          ) : (
            <div className="text-sm flex flex-col gap-3">
              {modal.body.data.map((section, i) => {
                return (
                  <p key={i}>
                    {section.header && (
                      <>
                        <span className="font-bold">{section.header}</span>
                        <br />
                      </>
                    )}
                    {section.body}
                  </p>
                )
              })}
            </div>
          )
        }
        setIsOpen={(v) =>
          !v && setModal((v) => (v ? { ...v, isOpen: false } : undefined))
        }
        // hideCloseButton
        actionButtons={
          modal == null
            ? []
            : [
                <div key={0} className="flex justify-items-stretch">
                  <Button
                    variant="functionWeak"
                    className="w-full"
                    onClick={() =>
                      setModal((v) => (v ? { ...v, isOpen: false } : undefined))
                    }
                  >
                    OK
                  </Button>
                </div>,
              ]
        }
      />
      <MemberSelectModal
        target="DinnerMember"
        title="同席者・共同幹事登録"
        isOpen={isInvitationModalOpen}
        onCancel={() => {
          setSelectedMembers(oldSelectedMembers)
          setIsInvitationModalOpen(false)
        }}
        onSubmit={() => {
          setIsInvitationModalOpen(false)
        }}
        memberTypes={[
          { label: '共同幹事', value: 'co_organizer' },
          { label: '同席者', value: 'attendee' },
        ]}
        members={props.dinner?.members ?? []}
        membersAdded={selectedMembers}
        onMemberAdded={(user, memberType) => {
          setSelectedMembers((v) => {
            return [...v, { user, memberType }]
          })
        }}
        onMemberChanged={(user, memberType) => {
          setSelectedMembers((v) => {
            return v.map((m) =>
              user.userId === m.user.userId
                ? { ...m, memberType: memberType }
                : m
            )
          })
        }}
        onMemberDeleted={(user) => {
          setSelectedMembers((v) => {
            return v.filter((x) => x.user.userId !== user.userId)
          })
        }}
      />
      <Container>
        <Heading variant="h2" component="h2">
          基本情報
        </Heading>

        <div className="my-5 md:mx-0">
          <form
            ref={formRef}
            onBlur={(e) => {
              const data = parseFormData(new FormData(e.currentTarget))
              if (data.success) {
                setFormError(undefined)
              } else {
                setFormError(data.error)
              }
            }}
            onChange={(e) => {
              const data = parseFormData(new FormData(e.currentTarget))
              if (data.success) {
                setFormError(undefined)
              } else {
                setFormError(data.error)
              }
            }}
            onSubmit={async (e) => {
              e.preventDefault()
              setTriedSubmit(true)

              const data = parseFormData(new FormData(e.currentTarget))

              // FIXME: startDate の扱いを後でリファクタする
              if (data.success) {
                setFormError(undefined)

                if (!scheduledDate) return
                const {
                  scheduled_at_hours,
                  scheduled_at_minutes,
                  approvement_scheduled_at_hours,
                  approvement_scheduled_at_minutes,
                  ...body
                } = data.data

                const scheduledAtJstStr = createJstDateStr(
                  scheduledDate,
                  scheduled_at_hours,
                  scheduled_at_minutes
                )
                // scheduled_at should be seconds, not milliseconds.
                const scheduled_at = Math.floor(
                  new Date(scheduledAtJstStr).valueOf() / 1000
                )

                const approvementScheduledAtJstStr =
                  approvementScheduledDate == null
                    ? undefined
                    : createJstDateStr(
                        approvementScheduledDate,
                        approvement_scheduled_at_hours,
                        approvement_scheduled_at_minutes
                      )
                const approvement_scheduled_at =
                  approvementScheduledAtJstStr == null
                    ? undefined
                    : Math.floor(
                        new Date(approvementScheduledAtJstStr).valueOf() / 1000
                      )

                props.onSubmit({
                  ...body,
                  scheduled_at: scheduled_at,
                  approvement_scheduled_at: approvement_scheduled_at,
                  invited_candidates:
                    props.dinner == null
                      ? selectedMembers.map((member) => {
                          return {
                            user_id: member.user.userId,
                            member_type: member.memberType,
                          }
                        })
                      : undefined,
                })
              } else {
                setFormError(data.error)
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }
            }}
          >
            <div className="flex flex-col mx-4 mb-8 gap-y-4">
              {hasError && triedSubmit && (
                <div className="text-sm text-[#C60017] mb-1">
                  入力内容に不備があります
                </div>
              )}
              <ShowOnly foodEnvs={[]}>
                {props.dinner == null && (
                  <div className="flex justify-center">
                    <button className="px-8 py-2 text-sm font-bold rounded-md center text-function bg-gray">
                      食べログ情報から自動入力
                    </button>
                  </div>
                )}
              </ShowOnly>
              <p className="text-[#AD3F4C] text-right text-xs font-bold">
                *は入力必須項目です。
              </p>
              <TextField
                label="食事会名"
                error={formError?.flatten().fieldErrors.dinner_name}
                inputProps={{
                  id: 'dinner_name',
                  name: 'dinner_name',
                  defaultValue: props.dinner?.dinnerName ?? '',
                }}
              />
              <div className="flex flex-col gap-y-2">
                <TextField
                  label="店名"
                  required
                  triedSubmit={triedSubmit}
                  error={formError?.flatten().fieldErrors.restaurant_name}
                  inputProps={{
                    id: 'restaurant_name',
                    name: 'restaurant_name',
                    defaultValue: props.dinner?.restaurantName ?? '',
                    onChange: (e) => {
                      setSearchRestaurantsQuery(e.target.value ?? '')
                    },
                  }}
                  alternativeInput={
                    restaurant ? (
                      <div className="flex items-center gap-x-4">
                        {restaurant.restaurantName}
                        {restaurant.areaName != null
                          ? `（${restaurant.areaName}）`
                          : ''}
                        {/* 作成済みの食事会が既にレストランと紐付いている場合は変更できない */}
                        {props.dinner?.restaurant == null && (
                          <button
                            type="button"
                            onClick={() => setRestaurant(undefined)}
                            className="px-2 py-1 text-xs border rounded border-gray"
                          >
                            選択解除
                          </button>
                        )}
                      </div>
                    ) : undefined
                  }
                />

                {restaurant == null &&
                  searchRestaurants.data &&
                  searchRestaurants.data.restaurants.length > 0 && (
                    <div>
                      <div className="font-bold text-xs mb-1 text-[#666]">
                        検索結果（{searchRestaurants.data.restaurants.length}）
                      </div>
                      <div className="rounded border border-gray max-h-[200px] overflow-y-scroll">
                        {searchRestaurants.data.restaurants.map(
                          (restaurant, i, restaurants) => {
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  setRestaurant(restaurant)

                                  if (
                                    restaurant.minBudgetAmount != null &&
                                    restaurant.maxBudgetAmount != null
                                  ) {
                                    setMinBudgetAmount(
                                      String(restaurant.minBudgetAmount)
                                    )
                                    setMaxBudgetAmount(
                                      String(restaurant.maxBudgetAmount)
                                    )
                                  } else {
                                    const singleBudgetAmount =
                                      restaurant.minBudgetAmount ??
                                      restaurant.maxBudgetAmount
                                    if (singleBudgetAmount) {
                                      setMinBudgetAmount(
                                        String(singleBudgetAmount)
                                      )
                                      setMaxBudgetAmount(
                                        String(singleBudgetAmount)
                                      )
                                    }
                                  }
                                }}
                                key={restaurant.restaurantId}
                                className={cx(
                                  'block w-full text-left px-3 py-2',
                                  restaurants.length - 1 !== i &&
                                    'border-b border-gray'
                                )}
                              >
                                {restaurant.restaurantName}
                                {restaurant.areaName != null
                                  ? `（${restaurant.areaName}）`
                                  : ''}
                              </button>
                            )
                          }
                        )}
                      </div>
                    </div>
                  )}
              </div>
              <div ref={onOpenScheduledAtCalendarAnchor}>
                <Label label="開始日時" required />
                <div className="flex items-end space-x-3">
                  <label
                    className="relative w-full"
                    onClick={() => {
                      setIsScheduledAtDatepickerOpen((v) => {
                        const newValue = !v
                        if (
                          newValue &&
                          onOpenScheduledAtCalendarAnchor.current
                        ) {
                          const anchorTop =
                            onOpenScheduledAtCalendarAnchor.current.getBoundingClientRect()
                              .top
                          const bodyTop =
                            document.body.getBoundingClientRect().top
                          window.scrollTo({
                            top:
                              anchorTop - bodyTop - parseInt(headerHeight) - 8,
                            behavior: 'smooth',
                          })
                        }
                        return newValue
                      })
                    }}
                  >
                    <input
                      name="scheduled_at_date"
                      type="text"
                      className={`px-4 border border-gray rounded text-sm h-[40px] active:border-gray outline-0 w-full`}
                      readOnly
                      onChange={() => {}}
                      value={
                        scheduledDate
                          ? formatDate(
                              { msec: scheduledDate.getTime() },
                              { omitCurrentYear: false, showLocalDate: true }
                            )
                          : ''
                      }
                    />
                    <div className="absolute top-0 bottom-0 right-0">
                      <div className="flex items-center h-full mr-4">
                        <CalendarIcon className="w-4 h-4" />
                      </div>
                    </div>
                  </label>

                  <div className="flex items-end gap-x-1">
                    <SelectV2
                      triggerOpen={
                        openScheduledAtControlledSelect != null
                          ? openScheduledAtControlledSelect === 'hours'
                          : undefined
                      }
                      onChange={() =>
                        setOpenScheduledAtControlledSelect('minutes')
                      }
                      name="scheduled_at_hours"
                      defaultValue={
                        scheduledAtHours ? String(scheduledAtHours) : '18'
                      }
                      placeholder="0"
                      options={Array.apply(null, { length: 24 } as any).map(
                        (_, i) => ({ label: String(i), value: String(i) })
                      )}
                    />
                    <span className="pb-1 text-xs">時</span>
                  </div>
                  <div className="flex items-end gap-x-1">
                    <SelectV2
                      triggerOpen={
                        openScheduledAtControlledSelect != null
                          ? openScheduledAtControlledSelect === 'minutes'
                          : undefined
                      }
                      onChange={() =>
                        setOpenScheduledAtControlledSelect(undefined)
                      }
                      name="scheduled_at_minutes"
                      defaultValue={
                        scheduledAtMinutes ? String(scheduledAtMinutes) : '0'
                      }
                      placeholder="0"
                      options={Array.apply(null, { length: 60 / 5 } as any).map(
                        (_, i) => ({
                          label: String(i * 5).padStart(2, '0'),
                          value: String(i * 5),
                        })
                      )}
                    />
                    <span className="pb-1 text-xs">分</span>
                  </div>
                </div>
                {flattenErrors?.scheduled_at_hours != null && (
                  <div className="text-sm text-[#C60017] mt-1">
                    {flattenErrors.scheduled_at_hours[0]}
                  </div>
                )}
              </div>
              {isScheduledAtDatepickerOpen && (
                <DatePicker
                  startDate={scheduledDate}
                  setStartDate={(v) => {
                    setScheduledDate(v)
                    setOpenScheduledAtControlledSelect('hours')
                  }}
                  setIsDatepickerOpen={setIsScheduledAtDatepickerOpen}
                />
              )}
              <div className="inline-flex">
                <SelectV2
                  name="reservation_number"
                  defaultValue={
                    props.dinner?.reservationNumber
                      ? String(props.dinner?.reservationNumber)
                      : '4'
                  }
                  placeholder="1"
                  options={Array.apply(null, { length: 100 } as any).map(
                    (_, i) => ({
                      label: String(i + 1),
                      value: String(i + 1),
                    })
                  )}
                  label="予約人数"
                  required
                  error={formError?.flatten().fieldErrors.reservation_number}
                />
              </div>
              <div>
                <Label label="募集人数（自分以外の人数）" />
                <div className="flex">
                  <SelectV2
                    name="recruitment_number"
                    defaultValue={
                      props.dinner?.recruitmentNumber
                        ? String(props.dinner?.recruitmentNumber)
                        : '1'
                    }
                    placeholder="1"
                    options={Array.apply(null, { length: 100 } as any).map(
                      (_, i) => ({
                        label: String(i + 1),
                        value: String(i + 1),
                      })
                    )}
                  />
                </div>
              </div>
              <div>
                <Label label="金額" />
                <div className="flex items-center space-x-2">
                  {isFixedBudgetAmount ? (
                    <>
                      <TextField
                        triedSubmit={triedSubmit}
                        error={
                          formError?.flatten().fieldErrors.fixed_budget_amount
                        }
                        inputProps={{
                          id: 'fixed_budget_amount',
                          name: 'fixed_budget_amount',
                          defaultValue: props.dinner
                            ? props.dinner.fixedBudgetAmount ?? ''
                            : '20000',
                        }}
                      />
                      <span>円</span>
                    </>
                  ) : (
                    <>
                      <SelectV2
                        name="min_budget_amount"
                        value={minBudgetAmount}
                        placeholder="指定なし"
                        options={[
                          { value: '', label: '指定なし' },
                          ...budgetOptions,
                        ]}
                        onChange={(v) => {
                          setMinBudgetAmount(v)
                          if (v === '') {
                            setMaxBudgetAmount('')
                          } else {
                            if (maxBudgetAmount === '') {
                              setMaxBudgetAmount(v)
                            }
                          }
                        }}
                      />
                      <span>〜</span>
                      <SelectV2
                        name="max_budget_amount"
                        value={maxBudgetAmount}
                        placeholder="指定なし"
                        options={[
                          { value: '', label: '指定なし' },
                          ...budgetOptions,
                        ]}
                        onChange={(v) => {
                          setMaxBudgetAmount(v)
                          if (v === '') {
                            setMinBudgetAmount('')
                          } else {
                            if (minBudgetAmount === '') {
                              setMinBudgetAmount(v)
                            }
                          }
                        }}
                      />
                      <span>円</span>
                    </>
                  )}
                </div>
                <div className="mt-2">
                  <Checkbox
                    id="amount"
                    label="固定金額で設定する"
                    defaultChecked={isFixedBudgetAmount}
                    onCheckedChange={(v) =>
                      v !== 'indeterminate' && setIsFixedBudgetAmount(v)
                    }
                  />
                  <div className="text-sm mt-1">
                    ※
                    金額が30万円を超える場合、自由記載欄に想定金額をご記載ください。
                  </div>
                </div>
                {isNaN(minBudgetAmountNumber) &&
                  !isNaN(maxBudgetAmountNumber) && (
                    <div className="text-sm text-[#C60017] mt-1">
                      下限金額を入力してください
                    </div>
                  )}
                {!isNaN(minBudgetAmountNumber) &&
                  isNaN(maxBudgetAmountNumber) && (
                    <div className="text-sm text-[#C60017] mt-1">
                      上限金額を入力してください
                    </div>
                  )}
                {!isNaN(minBudgetAmountNumber) &&
                  !isNaN(maxBudgetAmountNumber) &&
                  minBudgetAmountNumber > maxBudgetAmountNumber && (
                    <div className="text-sm text-[#C60017] mt-1">
                      予算金額を正しく入力してください
                    </div>
                  )}
              </div>
              <TextField
                label="住所"
                required
                triedSubmit={triedSubmit}
                error={formError?.flatten().fieldErrors.restaurant_address}
                inputProps={{
                  id: 'restaurant_address',
                  name: 'restaurant_address',
                  defaultValue:
                    props.dinner?.restaurantAddress ??
                    restaurant?.address ??
                    '',
                }}
              />
              <TextField
                label={
                  <>
                    URL
                    <span className="text-xs text-gray">(半角英数)</span>
                  </>
                }
                inputProps={{
                  id: 'restaurant_url',
                  name: 'restaurant_url',
                  defaultValue:
                    props.dinner?.restaurantUrl ??
                    restaurant?.tabelogRestaurantUrl ??
                    '',
                }}
              />
              <div>
                <div className="flex items-center gap-1">
                  <Label label="表示範囲" required />
                  <button
                    type="button"
                    onClick={() =>
                      setModal({
                        isOpen: true,
                        title: '表示範囲とは？',
                        body: {
                          type: 'sections',
                          data: [
                            {
                              header: '【全体公開】',
                              body: 'すべてのユーザーに食事会が公開されます。',
                            },
                            {
                              header: '【限定公開】',
                              body: '食事会作成後に発行される【招待用リンクURL】を知っているユーザーのみに公開されます。',
                            },
                            {
                              header: '【自分がフォローしている人だけに公開】',
                              body: 'フォローしている方にだけ公開されます。\n※フォロワーには公開されません',
                            },
                            {
                              body: '表示範囲は食事会作成後にも変更可能です。',
                            },
                          ],
                        },
                      })
                    }
                  >
                    <TipsIcon className="fill-[#999999]" />
                  </button>
                </div>
                <div className="flex">
                  <SelectV2
                    name="privacy_type"
                    defaultValue={props.dinner?.privacyType ?? 'public'}
                    placeholder="全体公開"
                    options={[
                      { label: '全体公開', value: 'public' },
                      { label: '限定公開', value: 'private' },
                      {
                        label: '自分がフォローしている人だけに公開',
                        value: 'following',
                      },
                    ]}
                  />
                </div>
              </div>
              {/* 新規作成時のみ表示 */}
              {props.dinner == null && (
                <div>
                  <Label label="同席者・共同幹事登録" />
                  <div
                    className={cx(
                      'px-4 py-4 border border-gray rounded text-xs text-[#6C6C6C] flex items-center justify-between gap-x-4  min-h-[45px] box-border'
                    )}
                    onClick={() => {
                      setOldSelectedMembers(selectedMembers)
                      setIsInvitationModalOpen(true)
                    }}
                  >
                    {selectedMembers.length === 0 ? (
                      <div></div>
                    ) : (
                      <div>
                        {selectedMembers.map((member) => {
                          return (
                            <div key={member.user.userId} className="my-1">
                              {`${member.user.displayName}  ${
                                dinnerMemberTypeLabels[member.memberType]
                              }`}
                            </div>
                          )
                        })}
                      </div>
                    )}
                    <ChevronRightIcon
                      className={cx(
                        'fill-[#63647B]',
                        props.dinner != null && 'hidden'
                      )}
                    />
                  </div>
                </div>
              )}
              <div>
                <Label label="写真" />
                <div className="flex grid-cols-3 gap-2 justify-start max-w-[400px]">
                  {!usingDinnerThumbnailImages &&
                    (linkedRestaurantThumbnailImages == null
                      ? Array.from(Array(3), (_, i) => (
                          <AppImage
                            key={i}
                            src={config.noImageUrl.restaurantCoverImage}
                            variant={{ type: 'cover', coverPosition: 'center' }}
                            width={110}
                            height={110}
                            rounded={'md'}
                            alt={`サムネイル画像${i + 1}`}
                          />
                        ))
                      : linkedRestaurantThumbnailImages.map(
                          (thumbnailImage, i) => {
                            return (
                              <AppImage
                                key={thumbnailImage?.imageId ?? i}
                                src={dinnerThumbnailImageOptToSrc(
                                  thumbnailImage
                                )}
                                variant={{
                                  type: 'cover',
                                  coverPosition: 'center',
                                }}
                                width={110}
                                height={110}
                                rounded={'md'}
                                alt={`サムネイル画像${i + 1}`}
                              />
                            )
                          }
                        ))}
                  {usingDinnerThumbnailImages &&
                    dinnerThumbnailImages.map((thumbnailImage, i) => {
                      return (
                        <div className="relative" key={thumbnailImage.imageId}>
                          <button
                            type="button"
                            className="absolute z-[1] right-1 top-1 w-5 h-5 rounded-full bg-white bg-opacity-70 flex items-center justify-center"
                            onClick={() => {
                              setDinnerThumbnailImages((prev) =>
                                prev.filter((_, j) => i !== j)
                              )
                            }}
                          >
                            <CloseIcon className="fill-[#666] w-2 h-2" />
                          </button>
                          <AppImage
                            src={dinnerThumbnailImageToSrc(thumbnailImage)}
                            variant={{ type: 'cover', coverPosition: 'center' }}
                            width={110}
                            height={110}
                            rounded={'md'}
                            alt={`サムネイル画像${i + 1}`}
                          />
                        </div>
                      )
                    })}
                  {/* {selfUploadedThumbnailImages} */}
                  {usingDinnerThumbnailImages &&
                    dinnerThumbnailImages.length < 3 && (
                      <label>
                        <AddImage className="w-[110px] h-[110px]" useCamera2 />
                        <input
                          type="file"
                          accept="image/*,.heic,.heif,.dng"
                          multiple
                          className="hidden"
                          onInput={onInputThumnailImages}
                        />
                      </label>
                    )}
                </div>

                {uploadingImagesError != null && (
                  <div className="text-sm text-[#C60017] mt-2">
                    {uploadingImagesError}
                  </div>
                )}

                {usingDinnerThumbnailImages || (
                  <div className="flex justify-end mt-2">
                    <button
                      type="button"
                      className="underline text-sm"
                      onClick={() => setPreferDinnerThumbnailImages(true)}
                    >
                      オリジナル写真に変更する
                    </button>
                  </div>
                )}
              </div>
            </div>

            <Heading variant="h2" component="h2">
              詳細設定
            </Heading>
            <div className="flex flex-col mx-4 mt-2 gap-y-4">
              <>
                <div className="flex flex-col pt-4 pb-2 text-sm text-gray-500 gap-y-4">
                  <div className="flex">
                    <SelectV2
                      label={
                        <div className="flex items-center gap-1">
                          お酒の種類
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                isOpen: true,
                                title: 'お酒の種類とは？',
                                body: '特定のお酒を嗜む会など、参加条件にお酒の種類を指定したい場合に選択してください。',
                              })
                            }
                          >
                            <TipsIcon className="fill-[#999999]" />
                          </button>
                        </div>
                      }
                      name="favorite_alcohol_type"
                      defaultValue={
                        props.dinner?.favoriteAlcoholOption?.key ?? 'unselected'
                      }
                      placeholder="指定なし"
                      options={props.staticDinnerOptionListResponse.optionList.favoriteAlcoholOptions.map(
                        (option) => ({
                          label: option.name,
                          value: option.key,
                        })
                      )}
                    />
                  </div>

                  <div className="flex">
                    <SelectV2
                      name="alcohol_amount_type"
                      label={
                        <div className="flex items-center gap-1">
                          お酒の量
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                isOpen: true,
                                title: 'お酒の量とは？',
                                body: 'お酒を飲める方のみを募集したい場合は、飲める量の目安を選択してください。',
                              })
                            }
                          >
                            <TipsIcon className="fill-[#999999]" />
                          </button>
                        </div>
                      }
                      defaultValue={
                        props.dinner?.alcoholAmountOption?.key ?? 'unselected'
                      }
                      placeholder="指定なし"
                      options={props.staticDinnerOptionListResponse.optionList.alcoholAmountOptions.map(
                        (option) => ({
                          label: option.name,
                          value: option.key,
                        })
                      )}
                    />
                  </div>

                  <div className="flex">
                    <SelectV2
                      label={
                        <div className="flex items-center gap-1">
                          食べる量
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                isOpen: true,
                                title: 'お酒の量とは？',
                                body: 'コース料理などボリュームが多いお店で、量を食べられる方を募集したい場合は、食べる量の目安を選択してください。',
                              })
                            }
                          >
                            <TipsIcon className="fill-[#999999]" />
                          </button>
                        </div>
                      }
                      name="food_amount_type"
                      defaultValue={
                        props.dinner?.foodAmountOption?.key ?? 'unselected'
                      }
                      placeholder="指定なし"
                      options={props.staticDinnerOptionListResponse.optionList.foodAmountOptions.map(
                        (option) => ({
                          label: option.name,
                          value: option.key,
                        })
                      )}
                    />
                  </div>
                  <div className="flex">
                    <SelectV2
                      label="性別"
                      name="sex_type"
                      defaultValue={
                        props.dinner?.sexOption?.key ?? 'unselected'
                      }
                      placeholder="指定なし"
                      options={props.staticDinnerOptionListResponse.optionList.sexOptions.map(
                        (option) => ({ label: option.name, value: option.key })
                      )}
                    />
                  </div>
                  <div>
                    <Label label="年齢" />
                    <div className="flex items-center space-x-2">
                      <SelectV2
                        name="min_age"
                        defaultValue={
                          props.dinner?.minAge
                            ? String(props.dinner.minAge)
                            : ''
                        }
                        placeholder="指定なし"
                        options={[
                          { label: '指定なし', value: '' },
                          ...new Array(100).fill(0).map((_, i) => ({
                            value: String(20 + i),
                            label: `${20 + i}歳`,
                          })),
                        ]}
                      />
                      <span>〜</span>
                      <SelectV2
                        name="max_age"
                        defaultValue={
                          props.dinner?.maxAge
                            ? String(props.dinner.maxAge)
                            : ''
                        }
                        placeholder="指定なし"
                        options={[
                          { label: '指定なし', value: '' },
                          ...new Array(100).fill(0).map((_, i) => ({
                            value: String(20 + i),
                            label: `${20 + i}歳`,
                          })),
                        ]}
                      />
                    </div>
                  </div>
                  <div>
                    <Label label="支払い方法" />
                    <div className="grid justify-between grid-cols-3">
                      {props.staticDinnerOptionListResponse.optionList.paymentOptions
                        .filter((option) => option.key !== 'unselected')
                        .map((option) => {
                          return (
                            <Checkbox
                              id={`payment-option-${option.key}`}
                              key={option.key}
                              label={option.name}
                              checked={paymentOption === option.key}
                              onCheckedChange={(v) => {
                                if (v !== 'indeterminate') {
                                  setPaymentOption(
                                    v ? option.key : 'unselected'
                                  )
                                }
                              }}
                            />
                          )
                        })}
                    </div>
                  </div>

                  <div ref={onOpenApprovementScheduledAtCalendarAnchor}>
                    <Label
                      label={
                        <div className="flex items-center gap-1">
                          参加者決定の予定期日
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                isOpen: true,
                                title: '参加者決定の予定期日とは？',
                                body: '食事会に応募があったときに、参加者を決定する予定期日を設定することができます。参加者決定の予定期日を設定することにより、応募した方が、いつまでに参加できるのか、参加できないのかを知ることができ、安心して応募することができるようになります。また、予定期日の1週間前と1日前にプッシュ通知を受け取ることができます。',
                              })
                            }
                          >
                            <TipsIcon className="fill-[#999999]" />
                          </button>
                        </div>
                      }
                    />
                    <div className="flex items-end space-x-3">
                      <label
                        className="relative w-full"
                        onClick={() => {
                          setIsApprovementScheduledAtDatepickerOpen((v) => {
                            const newValue = !v
                            if (
                              newValue &&
                              onOpenApprovementScheduledAtCalendarAnchor.current
                            ) {
                              const anchorTop =
                                onOpenApprovementScheduledAtCalendarAnchor.current.getBoundingClientRect()
                                  .top
                              const bodyTop =
                                document.body.getBoundingClientRect().top
                              window.scrollTo({
                                top:
                                  anchorTop -
                                  bodyTop -
                                  parseInt(headerHeight) -
                                  8,
                                behavior: 'smooth',
                              })
                            }
                            return newValue
                          })
                        }}
                      >
                        <input
                          name="approvement_scheduled_at"
                          type="text"
                          className={`px-4 border border-gray rounded text-sm h-[40px] active:border-gray outline-0 w-full`}
                          readOnly
                          onChange={() => {}}
                          value={
                            approvementScheduledDate
                              ? formatDate(
                                  { msec: approvementScheduledDate.getTime() },
                                  { omitCurrentYear: false }
                                )
                              : ''
                          }
                        />
                        <div className="absolute top-0 bottom-0 right-0">
                          <div className="flex items-center h-full mr-4">
                            <CalendarIcon className="w-4 h-4" />
                          </div>
                        </div>
                      </label>

                      <div className="flex items-end gap-x-1">
                        <SelectV2
                          triggerOpen={
                            openApprovementScheduledAtControlledSelect != null
                              ? openApprovementScheduledAtControlledSelect ===
                                'hours'
                              : undefined
                          }
                          onChange={() =>
                            setOpenApprovementScheduledAtControlledSelect(
                              'minutes'
                            )
                          }
                          name="approvement_scheduled_at_hours"
                          defaultValue={String(approvementScheduledAtHours)}
                          placeholder="0"
                          options={Array.apply(null, { length: 24 } as any).map(
                            (_, i) => ({ label: String(i), value: String(i) })
                          )}
                        />
                        <span className="pb-1 text-xs">時</span>
                      </div>
                      <div className="flex items-end gap-x-1">
                        <SelectV2
                          triggerOpen={
                            openApprovementScheduledAtControlledSelect != null
                              ? openApprovementScheduledAtControlledSelect ===
                                'minutes'
                              : undefined
                          }
                          onChange={() =>
                            setOpenApprovementScheduledAtControlledSelect(
                              undefined
                            )
                          }
                          name="approvement_scheduled_at_minutes"
                          defaultValue={String(approvementScheduledAtMinutes)}
                          placeholder="0"
                          options={Array.apply(null, {
                            length: 60 / 5,
                          } as any).map((_, i) => ({
                            label: String(i * 5).padStart(2, '0'),
                            value: String(i * 5),
                          }))}
                        />
                        <span className="pb-1 text-xs">分</span>
                      </div>
                    </div>
                    {flattenErrors?.approvement_scheduled_at_hours != null && (
                      <div className="text-sm text-[#C60017] mt-1">
                        {flattenErrors.approvement_scheduled_at_hours[0]}
                      </div>
                    )}
                  </div>
                  {isApprovementScheduledAtDatepickerOpen && (
                    <DatePicker
                      startDate={approvementScheduledDate}
                      setStartDate={(v) => {
                        setApprovementScheduledDate(v)
                        setOpenApprovementScheduledAtControlledSelect('hours')
                      }}
                      setIsDatepickerOpen={
                        setIsApprovementScheduledAtDatepickerOpen
                      }
                    />
                  )}

                  <TextArea
                    label={
                      <span className="flex justify-between items-center">
                        自由記載
                        <DictionaryDialog
                          onSelect={(text) => {
                            if (noteInputRef.current) {
                              noteInputRef.current.value = noteInputRef.current
                                .value
                                ? noteInputRef.current.value + '\n' + text
                                : text
                            }
                          }}
                        />
                      </span>
                    }
                    dynamicHeight
                    textareaClassName="max-h-[50vh]"
                    error={formError?.flatten().fieldErrors.note}
                    inputProps={{
                      ref: noteInputRef,
                      name: 'note',
                      defaultValue: props.dinner?.note ?? '',
                    }}
                  />

                  <TextArea
                    label="自分用メモ（他の人には公開されません）"
                    dynamicHeight
                    textareaClassName="max-h-[50vh]"
                    error={formError?.flatten().fieldErrors.memo_text}
                    inputProps={{
                      name: 'memo_text',
                      defaultValue: props.dinnerMemo?.text ?? '',
                    }}
                  />

                  {/*
                  <div>
                    <Label
                      label={
                        <div className="flex items-center gap-1">
                          キャンセルポリシー1
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                isOpen: true,
                                title: 'キャンセルポリシーとは',
                                body: '参加者からキャンセルがあった場合、設定したキャンセルポリシーに基づいてキャンセル金額を保証することができます。（キャンセルポリシーの金額が、食事会の代金を超える場合は無効となりますのでこ注意ください。）',
                              })
                            }
                          >
                            <TipsIcon className="fill-[#999999]" />
                          </button>
                        </div>
                      }
                    />
                    <div className="flex items-center space-x-2">
                      <SelectV2
                        fullWidth
                        name="-1"
                        defaultValue={''}
                        placeholder="指定なし"
                        options={[{ label: '指定なし', value: '' }]}
                      />

                      <div className="w-[135px]">
                        <SelectV2
                          fullWidth
                          name="-a1"
                          defaultValue={''}
                          placeholder="指定なし"
                          options={[
                            { label: '指定なし', value: '' },
                            ...budgetOptions,
                          ]}
                        />
                      </div>
                      <span>P</span>
                    </div>
                  </div>
                  <div>
                    <Label
                      label={
                        <div className="flex items-center gap-1">
                          キャンセルポリシー2
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                isOpen: true,
                                title: 'キャンセルポリシーとは',
                                body: '参加者からキャンセルがあった場合、設定したキャンセルポリシーに基づいてキャンセル金額を保証することができます。（キャンセルポリシーの金額が、食事会の代金を超える場合は無効となりますのでこ注意ください。）',
                              })
                            }
                          >
                            <TipsIcon className="fill-[#999999]" />
                          </button>
                        </div>
                      }
                    />
                    <div className="flex items-center space-x-2">
                      <SelectV2
                        fullWidth
                        name="-2"
                        defaultValue={''}
                        placeholder="指定なし"
                        options={[{ label: '指定なし', value: '' }]}
                      />
                      <div className="w-[135px]">
                        <SelectV2
                          fullWidth
                          name="-a2"
                          defaultValue={''}
                          placeholder="指定なし"
                          options={[
                            { label: '指定なし', value: '' },
                            ...budgetOptions,
                          ]}
                        />
                      </div>
                      <span>P</span>
                    </div>
                  </div>
                  <div>
                    <Label
                      label={
                        <div className="flex items-center gap-1">
                          キャンセルポリシー3
                          <button
                            type="button"
                            onClick={() =>
                              setModal({
                                isOpen: true,
                                title: 'キャンセルポリシーとは',
                                body: '参加者からキャンセルがあった場合、設定したキャンセルポリシーに基づいてキャンセル金額を保証することができます。（キャンセルポリシーの金額が、食事会の代金を超える場合は無効となりますのでこ注意ください。）',
                              })
                            }
                          >
                            <TipsIcon className="fill-[#999999]" />
                          </button>
                        </div>
                      }
                    />
                    <div className="flex items-center space-x-2">
                      <SelectV2
                        fullWidth
                        name="-3"
                        defaultValue={''}
                        placeholder="指定なし"
                        options={[{ label: '指定なし', value: '' }]}
                      />
                      <div className="w-[135px]">
                        <SelectV2
                          fullWidth
                          name="-a3"
                          defaultValue={''}
                          placeholder="指定なし"
                          options={[
                            { label: '指定なし', value: '' },
                            ...budgetOptions,
                          ]}
                        />
                      </div>
                      <span>P</span>
                    </div>
                  </div> */}
                </div>
              </>
            </div>
            {/* 食事会新規作成時のみ表示 */}
            {props.dinner == null && (
              <p className="mt-6 text-center text-black">
                <Link className="underline" href="/dinners/recruitment_rule">
                  使用上のルール
                </Link>
                を守って
              </p>
            )}

            <div className="flex justify-center my-10 space-x-4">
              <button
                type="button"
                className="w-40 py-2 rounded-md bg-gray"
                onClick={async () => {
                  if (props.dinner) {
                    await router.push(`/dinners`)
                  } else {
                    clearForm()
                  }
                }}
              >
                {props.dinner ? 'キャンセル' : 'クリア'}
              </button>
              <button
                type="submit"
                className="w-40 py-2 text-white rounded-md bg-secondary"
              >
                {props.dinner ? '変更を確定する' : '募集を開始する'}
              </button>
            </div>
          </form>
        </div>
      </Container>
    </div>
  )
}

export { DinnerForm }
